import React, { useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

export function InstallPromptBanner() {
  const { showPrompt, isInstalled, isIOS, dismissCount, install, dismiss, canInstall } = useInstallPrompt();
  const [neverAsk, setNeverAsk] = useState(false);

  if (!showPrompt || isInstalled) return null;

  const showNeverAskOption = dismissCount >= 2;

  return (
    <div
      data-testid="install-prompt-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-96 z-[9998] animate-slideUp"
    >
      <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-violet-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-192.png" alt="" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              ChildCareConnections
            </span>
          </div>
          <button
            data-testid="install-prompt-close"
            onClick={() => dismiss(neverAsk)}
            className="p-1 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm text-slate-700 font-medium mb-1">
            {isIOS ? 'Add to your Home Screen' : 'Install our app'}
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Get quick access, push notifications, and a full-screen experience — just like a native app.
          </p>

          {/* iOS instructions */}
          {isIOS && (
            <div className="mt-3 p-2.5 rounded-xl bg-slate-50 border border-slate-100">
              <p className="text-xs text-slate-600 font-medium mb-1.5">How to install:</p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  1. Tap <Share className="w-3.5 h-3.5 text-blue-500 inline" /> Share
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                <span className="flex items-center gap-1">
                  2. Scroll down and tap <Plus className="w-3.5 h-3.5 inline" /> <strong>Add to Home Screen</strong>
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-2">
            {canInstall && (
              <button
                data-testid="install-prompt-install"
                onClick={install}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-500 text-white text-sm font-medium hover:bg-violet-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                Install
              </button>
            )}
            <button
              data-testid="install-prompt-dismiss"
              onClick={() => dismiss(neverAsk)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors"
            >
              {isIOS ? 'Got it' : 'Not now'}
            </button>
          </div>

          {/* Don't Ask Again checkbox — only after 2 dismissals */}
          {showNeverAskOption && (
            <label className="flex items-center gap-2 mt-2.5 cursor-pointer" data-testid="install-prompt-never-ask">
              <input
                type="checkbox"
                checked={neverAsk}
                onChange={(e) => setNeverAsk(e.target.checked)}
                className="rounded border-slate-300 text-violet-500 focus:ring-violet-500"
              />
              <span className="text-xs text-slate-400">Don&apos;t ask again</span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
